import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./Icon";

export function Modal({
  eyebrow,
  title,
  titleId,
  children,
  focusKey,
  onClose,
  closeDisabled = false,
  wide = false,
}: {
  eyebrow: string;
  title: string;
  titleId: string;
  children: ReactNode;
  /** Names the current body; changing it re-runs the initial focus step. */
  focusKey?: string;
  onClose?: () => void;
  closeDisabled?: boolean;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // React unmounts the <dialog> before this cleanup runs, so the native
    // close() focus restore never fires; return focus to the opener ourselves.
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    if (!dialog.open) dialog.showModal();

    return () => {
      if (dialog.open) dialog.close();
      opener?.focus();
    };
  }, []);

  // Separate from opening the dialog: swapping the body replaces the focused
  // element, which would otherwise drop focus to <body>.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    // `autoFocus` never fires here: React commits it while the dialog is still
    // closed, so the first field opts in with `data-autofocus` instead.
    window.requestAnimationFrame(() =>
      (
        dialog.querySelector<HTMLElement>("[data-autofocus]") ??
        titleRef.current
      )?.focus(),
    );
  }, [focusKey]);

  return (
    <dialog
      aria-labelledby={titleId}
      className={`dialog${wide ? " dialog--wide" : ""}`}
      onCancel={(event) => {
        event.preventDefault();
        if (!closeDisabled) onClose?.();
      }}
      ref={dialogRef}
    >
      <header className="dialog__header">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2 id={titleId} ref={titleRef} tabIndex={-1}>
            {title}
          </h2>
        </div>
        {onClose && (
          <button
            aria-label="Close dialog"
            className="icon-button icon-button--surface"
            disabled={closeDisabled}
            onClick={onClose}
            type="button"
          >
            <Icon name="close" />
          </button>
        )}
      </header>
      {children}
    </dialog>
  );
}
